"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createCustomAppDataSourceBuildingBlockRef = createCustomAppDataSourceBuildingBlockRef;
exports.createCustomAppActionBuildingBlockRef = createCustomAppActionBuildingBlockRef;
exports.createCustomAppCapabilityBuildingBlockRef = createCustomAppCapabilityBuildingBlockRef;
exports.createCustomAppDocumentExportBuildingBlockRef = createCustomAppDocumentExportBuildingBlockRef;
exports.validateCustomAppBuildingBlockRef = validateCustomAppBuildingBlockRef;
exports.isRecognizedCustomAppBuildingBlockRef = isRecognizedCustomAppBuildingBlockRef;
const brand_1 = require("@streamlain/brand");
const custom_app_contract_1 = require("./custom-app-contract");
const STREAMLAIN_BUILDING_BLOCK_FAMILIES = new Set([
    'streamlain.datasource',
    'streamlain.action',
    'streamlain.capability',
    'streamlain.documentExport',
]);
const STREAMLAIN_BUILDING_BLOCK_KIND_BY_FAMILY = new Map([
    ['streamlain.datasource', 'dataSource'],
    ['streamlain.action', 'action'],
    ['streamlain.capability', 'capability'],
    ['streamlain.documentExport', 'documentExport'],
]);
const STREAMLAIN_ACTION_INTENTS = new Set([
    'rowInsert',
    'automationRun',
    'attachmentUpdate',
]);
function createCustomAppDataSourceBuildingBlockRef(input) {
    return parseBuildingBlockRef({
        id: `streamlain.datasource.${input.role}.${input.contentModel}.${input.sourceKey}`,
        kind: 'dataSource',
        family: 'streamlain.datasource',
        label: input.label,
        sourceKey: input.sourceKey,
        metadata: {
            role: input.role,
            contentModel: input.contentModel,
            ...(input.audience ? { audience: input.audience } : {}),
            writable: input.writable === true,
        },
    });
}
function createCustomAppActionBuildingBlockRef(input) {
    return parseBuildingBlockRef({
        id: `streamlain.action.${input.intent}.${input.actionId}`,
        kind: 'action',
        family: 'streamlain.action',
        label: input.label,
        actionId: input.actionId,
        sourceKey: input.sourceKey,
        metadata: {
            intent: input.intent,
        },
    });
}
function createCustomAppCapabilityBuildingBlockRef(input) {
    const scope = input.actionId ?? input.sourceKey ?? input.exportId;
    return parseBuildingBlockRef({
        id: scope
            ? `streamlain.capability.${input.capabilityId}.${scope}`
            : `streamlain.capability.${input.capabilityId}`,
        kind: 'capability',
        family: 'streamlain.capability',
        label: input.label ?? input.capabilityId,
        capabilityId: input.capabilityId,
        actionId: input.actionId,
        sourceKey: input.sourceKey,
        exportId: input.exportId,
    });
}
function createCustomAppDocumentExportBuildingBlockRef(input) {
    return parseBuildingBlockRef({
        id: `streamlain.documentExport.${input.formats.join('+')}.${input.exportId}`,
        kind: 'documentExport',
        family: 'streamlain.documentExport',
        label: input.label,
        exportId: input.exportId,
        actionId: input.targetActionId,
        metadata: {
            formats: [...input.formats],
        },
    });
}
function parseBuildingBlockRef(input) {
    return custom_app_contract_1.CustomAppBuildingBlockRefSchema.parse({
        schemaVersion: custom_app_contract_1.CUSTOM_APP_BUILDING_BLOCK_REF_SCHEMA_VERSION,
        ...input,
    });
}
function validateCustomAppBuildingBlockRef(ref, path = 'buildingBlockRefs') {
    const issues = [];
    const family = ref.family && STREAMLAIN_BUILDING_BLOCK_FAMILIES.has(ref.family)
        ? ref.family
        : deriveBuildingBlockFamily(ref.id);
    if (!family) {
        issues.push({
            path,
            message: `Custom app building block ref ${ref.id} must use a ${brand_1.BRAND_DISPLAY_NAME} building block family.`,
        });
        return issues;
    }
    const expectedKind = STREAMLAIN_BUILDING_BLOCK_KIND_BY_FAMILY.get(family);
    if (expectedKind && ref.kind !== expectedKind) {
        issues.push({
            path: `${path}.kind`,
            message: `Custom app building block ref ${ref.id} must use kind ${expectedKind}.`,
        });
    }
    if (family === 'streamlain.datasource') {
        validateDataSourceRef(ref, path, issues);
    }
    else if (family === 'streamlain.action') {
        validateActionRef(ref, path, issues);
    }
    else if (family === 'streamlain.capability') {
        validateCapabilityRef(ref, path, issues);
    }
    else if (family === 'streamlain.documentExport') {
        validateDocumentExportRef(ref, path, issues);
    }
    return issues;
}
function isRecognizedCustomAppBuildingBlockRef(ref) {
    return validateCustomAppBuildingBlockRef(ref).length === 0;
}
function deriveBuildingBlockFamily(id) {
    for (const family of STREAMLAIN_BUILDING_BLOCK_FAMILIES) {
        if (id === family || id.startsWith(`${family}.`))
            return family;
    }
    return null;
}
function validateDataSourceRef(ref, path, issues) {
    const role = ref.metadata?.['role'];
    const contentModel = ref.metadata?.['contentModel'];
    if (!ref.sourceKey) {
        issues.push({
            path: `${path}.sourceKey`,
            message: `Data source building block ${ref.id} must declare sourceKey.`,
        });
    }
    if (typeof role !== 'string' || typeof contentModel !== 'string') {
        issues.push({
            path: `${path}.metadata`,
            message: `Data source building block ${ref.id} must declare role and contentModel metadata.`,
        });
        return;
    }
    const expectedId = ref.sourceKey
        ? `streamlain.datasource.${role}.${contentModel}.${ref.sourceKey}`
        : null;
    if (expectedId && ref.id !== expectedId) {
        issues.push({
            path: `${path}.id`,
            message: `Data source building block ${ref.id} must use canonical id ${expectedId}.`,
        });
    }
}
function validateActionRef(ref, path, issues) {
    const intent = ref.metadata?.['intent'];
    if (!ref.actionId) {
        issues.push({
            path: `${path}.actionId`,
            message: `Action building block ${ref.id} must declare actionId.`,
        });
    }
    if (typeof intent !== 'string' || !STREAMLAIN_ACTION_INTENTS.has(intent)) {
        issues.push({
            path: `${path}.metadata.intent`,
            message: `Action building block ${ref.id} must declare a supported action intent.`,
        });
    }
    const expectedId = typeof intent === 'string' && ref.actionId
        ? `streamlain.action.${intent}.${ref.actionId}`
        : null;
    if (expectedId && ref.id !== expectedId) {
        issues.push({
            path: `${path}.id`,
            message: `Action building block ${ref.id} must use canonical id ${expectedId}.`,
        });
    }
}
function validateCapabilityRef(ref, path, issues) {
    if (!ref.capabilityId) {
        issues.push({
            path: `${path}.capabilityId`,
            message: `Capability building block ${ref.id} must declare capabilityId.`,
        });
        return;
    }
    if (!ref.id.startsWith(`streamlain.capability.${ref.capabilityId}`)) {
        issues.push({
            path: `${path}.id`,
            message: `Capability building block ${ref.id} must start with streamlain.capability.${ref.capabilityId}.`,
        });
    }
    const expectedScope = ref.actionId ?? ref.sourceKey ?? ref.exportId;
    const expectedId = expectedScope
        ? `streamlain.capability.${ref.capabilityId}.${expectedScope}`
        : `streamlain.capability.${ref.capabilityId}`;
    if (ref.id !== expectedId) {
        issues.push({
            path: `${path}.id`,
            message: `Capability building block ${ref.id} must use canonical id ${expectedId}.`,
        });
    }
    if ((ref.capabilityId === 'dataset.readWindow' ||
        ref.capabilityId === 'dataset.subscribe') &&
        !ref.sourceKey) {
        issues.push({
            path: `${path}.sourceKey`,
            message: `Dataset capability building block ${ref.id} must declare sourceKey.`,
        });
    }
    if ((ref.capabilityId === 'database.row.insert' ||
        ref.capabilityId === 'agentAction.invoke' ||
        ref.capabilityId === 'attachment.update' ||
        ref.capabilityId === 'automation.run') &&
        !ref.actionId) {
        issues.push({
            path: `${path}.actionId`,
            message: `Action capability building block ${ref.id} must declare actionId.`,
        });
    }
    if (ref.capabilityId === 'document.export' &&
        !ref.actionId &&
        !ref.exportId) {
        issues.push({
            path: `${path}.exportId`,
            message: `Document export capability building block ${ref.id} must declare exportId or actionId.`,
        });
    }
}
function validateDocumentExportRef(ref, path, issues) {
    const formats = ref.metadata?.['formats'];
    if (!ref.exportId) {
        issues.push({
            path: `${path}.exportId`,
            message: `Document export building block ${ref.id} must declare exportId.`,
        });
    }
    if (!Array.isArray(formats) ||
        formats.length === 0 ||
        !formats.every(format => typeof format === 'string' && format.length > 0)) {
        issues.push({
            path: `${path}.metadata.formats`,
            message: `Document export building block ${ref.id} must declare formats metadata.`,
        });
        return;
    }
    const expectedId = ref.exportId
        ? `streamlain.documentExport.${formats.join('+')}.${ref.exportId}`
        : null;
    if (expectedId && ref.id !== expectedId) {
        issues.push({
            path: `${path}.id`,
            message: `Document export building block ${ref.id} must use canonical id ${expectedId}.`,
        });
    }
}
